import { Tooltip, Typography } from '@mui/material';
import { colors } from 'crono-fe-common/theme';
import { useMemo } from 'react';
import { CreditsBadgeWrapper } from './style';
import useGetUserCredits from 'hooks/services/user/useGetUserCredits';
import { useNavigate } from 'react-router-dom';
import PATH from 'crono-fe-common/constants/path';
import { format } from 'date-fns';
import { cn } from 'utils/style-utils';
import IconFindPhone from 'crono-fe-common/icons/Icon-Find-Phone';
import IconFindEmail from 'crono-fe-common/icons/Icon-Find-Email';
import IconRewriteCredits from 'crono-fe-common/icons/Icon-Rewrite-Credits';
import { UserCredits } from 'crono-fe-common/types/userCredits';

export type CreditType =
  | 'phone'
  | 'email'
  | 'linkedin'
  | 'companyEnrich'
  | 'rewrite';

export const creditTypesGeneralEnrichment = [
  'email',
  'linkedin',
  'companyEnrich',
];

interface IProps {
  creditsNeededForAction: number;
  creditsType: CreditType;
  disabled?: boolean;
  /**
   * Show only how many credits are left, without the count the action uses.
   * AI credits otherwise show just the icon.
   */
  showOnlyLeft?: boolean;
  /** Called once the user is taken to buy more credits. */
  onBuyMore?: () => void;
}

const CreditsBadge = ({
  creditsNeededForAction,
  creditsType,
  disabled,
  showOnlyLeft = false,
  onBuyMore,
}: IProps) => {
  const { data: creditsData } = useGetUserCredits();

  const credits: UserCredits | undefined = useMemo(
    () => creditsData?.data?.data,
    [creditsData],
  );

  const navigate = useNavigate();

  const handleBuyMoreCredits = () => {
    navigate(PATH.SETTINGS.ADD_ONS);
    onBuyMore?.();
  };

  const creditsLeft = useMemo(() => {
    if (!credits) return null;
    switch (creditsType) {
      case 'phone':
        return credits.phoneLeft;
      case 'rewrite':
        return credits.aiLeft;
      case 'email':
        return credits.emailLeft;
      case 'linkedin':
        // For now, we use email credits to find LinkedIn url
        return credits.emailLeft;
      case 'companyEnrich':
        // For now, we use email credits to enrich company
        return credits.emailLeft;
    }
  }, [credits, creditsType]);

  const creditsLeftLabel = useMemo(() => {
    switch (creditsType) {
      case 'phone':
        return 'Find phone credits left.';
      case 'rewrite':
        return 'Find AI credits left.';
      default:
        return 'Enrichment credits left.';
    }
  }, [creditsType]);

  const notEnoughCredits = useMemo(
    () => creditsLeft !== null && creditsLeft < creditsNeededForAction,
    [creditsLeft, creditsNeededForAction],
  );

  const creditsIcon = useMemo(() => {
    switch (creditsType) {
      case 'phone':
        return (
          <IconFindPhone
            color={notEnoughCredits ? colors.inactive : colors.findPhoneCredits}
            size={16}
          />
        );
      case 'rewrite':
        return (
          <IconRewriteCredits
            color={notEnoughCredits ? colors.inactive : colors.rewriteCredits}
            size={16}
          />
        );
      default:
        return (
          <IconFindEmail
            color={notEnoughCredits ? colors.inactive : colors.lightBlueHover}
            size={16}
          />
        );
    }
  }, [creditsType, notEnoughCredits]);

  // AI (rewrite) credits are consumption-based, so a per-action credit count
  // is no longer meaningful — show only the icon with an informational tooltip.
  if (creditsType === 'rewrite') {
    return (
      <CreditsBadgeWrapper>
        <Tooltip
          enterDelay={400}
          enterNextDelay={400}
          arrow
          placement="top"
          title={
            !disabled && (
              <span
                style={{
                  whiteSpace: 'pre-line',
                  display: 'flex',
                  textAlign: 'center',
                }}
              >
                This action uses AI credits.
                {'\n'}
                Next update:{' '}
                {format(
                  new Date(credits?.creditsRenewalDate ?? new Date()),
                  'MMM dd, yyyy',
                )}
              </span>
            )
          }
        >
          <div className={cn('credits credits--rewrite', { disabled })}>
            {creditsIcon}
            {showOnlyLeft && creditsLeft !== null && (
              <div className="remaining-credits">
                <Typography
                  fontSize={11}
                  lineHeight={'14px'}
                  fontWeight={500}
                  color={colors.grey11}
                >
                  {creditsLeft} left
                </Typography>
              </div>
            )}
          </div>
        </Tooltip>
      </CreditsBadgeWrapper>
    );
  }

  return creditsLeft !== null ? (
    <CreditsBadgeWrapper
      onClick={notEnoughCredits ? undefined : handleBuyMoreCredits}
    >
      <Tooltip
        enterDelay={400}
        enterNextDelay={400}
        arrow
        placement="top"
        title={
          !disabled && (
            <span
              style={{
                whiteSpace: 'pre-line',
                display: 'flex',
                textAlign: 'center',
              }}
            >
              {creditsLeft} {creditsLeftLabel}
              {'\n'}
              Next update:{' '}
              {format(
                new Date(credits?.creditsRenewalDate ?? new Date()),
                'MMM dd, yyyy',
              )}
            </span>
          )
        }
      >
        <div
          className={cn('credits', {
            'credits--insufficient': notEnoughCredits,
            'credits--phone': creditsType === 'phone',
            'credits--enrichment':
              creditTypesGeneralEnrichment.includes(creditsType),
            disabled: disabled,
          })}
        >
          {creditsIcon}
          {!showOnlyLeft && (
            <Typography fontSize={11} lineHeight={'14px'} fontWeight={500}>
              {creditsNeededForAction}{' '}
              {creditsNeededForAction === 1 ? 'credit' : 'credits'}
            </Typography>
          )}
          <div className="remaining-credits">
            <Typography
              fontSize={11}
              lineHeight={'14px'}
              fontWeight={500}
              color={colors.grey11}
            >
              {creditsLeft} left
            </Typography>
            {notEnoughCredits && (
              <div
                onClick={handleBuyMoreCredits}
                className={cn('buy-more-button', {
                  disabled: disabled,
                })}
              >
                <Typography
                  fontSize={11}
                  lineHeight={'14px'}
                  fontWeight={500}
                  color={colors.white}
                >
                  Buy
                </Typography>
              </div>
            )}
          </div>
        </div>
      </Tooltip>
    </CreditsBadgeWrapper>
  ) : null;
};

export default CreditsBadge;
